import { type ReactNode, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/format-currency';
import { bestOption, humanizeValidity, type SurfaceItem } from '@/lib/monetization';
import { formatHms } from './countdown';

export interface SurfaceCardProps {
  item: SurfaceItem;
  /** Opens the detail sheet for this surface (claim / deposit happens there). */
  onOpen: () => void;
}

// The chip is just the bucketed validity hint (no prefix); the surfaces API
// exposes `expirySeconds`, not an absolute deadline, so a true HH:MM:SS
// countdown is only shown when the API provides `endTime`.

/** Maps a resolved surface to the compact widget's label / subtitle / badge. */
function widgetCopy(item: SurfaceItem): { label: string; description: string; chip?: string } {
  const p = item.presentation ?? {};
  if (item.type === 'GIFT') {
    const gift = item.gift ?? null;
    return {
      label: p.title ?? p.headline ?? 'Gift',
      description: p.subtitle ?? p.body ?? (gift ? `${formatCurrency(gift.amountCents)} waiting!` : 'Daily reward waiting!'),
      chip: humanizeValidity(gift?.expirySeconds),
    };
  }
  if (item.type === 'OFFER') {
    const best = bestOption(item.offer?.options ?? []);
    return {
      label: p.title ?? p.headline ?? 'Offer',
      description: p.subtitle ?? p.body ?? (best ? `+${best.bonusPercent}% deposit bonus` : 'Deposit bonus offer'),
      chip: humanizeValidity(best?.expirySeconds),
    };
  }
  return {
    label: p.title ?? p.headline ?? 'Update',
    description: p.subtitle ?? p.body ?? 'Tap to view',
  };
}

/**
 * Compact promo widget for the home offer strip (Figma: Widget, 1854-56884).
 *
 * Fixed 150×74 layout: lavender card, 50×50 thumb, title + subtitle, optional
 * gradient validity badge overlapping the bottom edge. The whole card is a
 * button — tapping it opens the {@link SurfaceDetailSheet} where the player
 * claims the gift / opens the deposit offer.
 */
export function SurfaceCard({ item, onOpen }: SurfaceCardProps) {
  const { label, description, chip } = widgetCopy(item);
  // `imageUrl` rides on the free-form presentation blob (index signature).
  const imageUrl = typeof item.presentation?.imageUrl === 'string' ? item.presentation.imageUrl : undefined;

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${label} — ${description}`}
      className="relative block h-[74px] w-[150px] text-left"
      data-surface-type={item.type}
    >
      <div
        className={cn(
          'absolute left-0 top-0 flex h-[63px] w-[150px] flex-row items-center gap-1 rounded-xl border-2 border-stroke-soft bg-input-soft pb-[9px] pl-1 pr-2 pt-[5px]',
          'shadow-[0px_0px_4px_0px_rgba(230,225,240,1)]',
        )}
      >
        <div className="h-[50px] w-[50px] shrink-0 overflow-hidden rounded-lg">
          {imageUrl ? (
            <img src={imageUrl} alt="" className="size-full object-cover" />
          ) : (
            <div className="size-full bg-linear-to-br from-accent-soft-light to-accent-soft" />
          )}
        </div>

        <div className="flex min-h-[42px] min-w-0 flex-1 flex-col justify-center gap-0 leading-tight">
          <span className="text-[14px] font-semibold leading-[1.1] text-ink">{label}</span>
          <span className="whitespace-pre-line text-[12px] font-normal leading-[1.15] text-ink-muted">{description}</span>
        </div>
      </div>

      {item.endTime ? <CountdownBadge endTime={item.endTime} /> : chip ? <BadgePill>{chip}</BadgePill> : null}
    </button>
  );
}

/** The gradient pill that overlaps the widget's bottom edge (Figma: Widget). */
function BadgePill({ children }: { children: ReactNode }) {
  return (
    <div className="absolute left-[45px] top-[54px] flex h-[18px] w-[60px] items-center justify-center rounded-2xl bg-[linear-gradient(135deg,var(--color-brand-grad-3)_0%,var(--color-brand-grad-2)_50%,var(--color-brand-grad-1)_100%)] px-1.5">
      <span className="text-center text-[12px] font-semibold leading-none text-white">{children}</span>
    </div>
  );
}

/**
 * Live `HH:MM:SS` countdown to the surface's delivery deadline, ticking once a
 * second. Hides itself once the deadline passes (the surface is normally gone
 * from the feed by then anyway).
 */
function CountdownBadge({ endTime }: { endTime: string }) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const hms = formatHms(endTime, nowMs);
  if (!hms) return null;
  return <BadgePill>{hms}</BadgePill>;
}
